import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SaveIcon from "@mui/icons-material/Save";
import ErrorModal from "../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const ROLES = ["PROJECT_HEAD", "SALESPERSON", "TELECALLER"];
const ROLE_LABELS = {
  PROJECT_HEAD: "Project Head",
  SALESPERSON: "Salesperson",
  TELECALLER: "Telecaller",
};

const mandatory = [
  "name",
  "phone",
  "alternatePhone",
  "isWhatsapp",
  "isAlternateWhatsapp",
  "email",
  "nextFollowUp",
  "lastFollowUp",
];

const LeadFieldPermission = ({ projectId }) => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const headers = { Authorization: `Bearer ${storedUser?.accessToken}` };

  const [fields, setFields] = useState([]);
  // { PROJECT_HEAD: Set<fieldName>, SALESPERSON: Set<fieldName>, TELECALLER: Set<fieldName> }
  const [selectedFields, setSelectedFields] = useState({
    PROJECT_HEAD: new Set(),
    SALESPERSON: new Set(),
    TELECALLER: new Set(),
  });
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [permissionsExist, setPermissionsExist] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const structureRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-structure?projectId=${projectId}`,
          { headers },
        );
        const { fieldNames = [], customFields = [] } = structureRes.data || {};
        const allFields = [
          ...fieldNames,
          ...customFields.map((f) => f.fieldName),
        ];
        setFields(allFields);

        let permissionData = [];
        try {
          const permRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/permission?projectId=${projectId}`,
            { headers },
          );
          permissionData = Array.isArray(permRes.data) ? permRes.data : [];
          setPermissionsExist(permissionData.length > 0);
        } catch {
          permissionData = [];
          setPermissionsExist(false);
        }

        // Build sets from saved permissions
        const sets = {
          PROJECT_HEAD: new Set(),
          SALESPERSON: new Set(),
          TELECALLER: new Set(),
        };
        permissionData.forEach(({ fieldName, userRoleName }) => {
          if (sets[userRoleName]) sets[userRoleName].add(fieldName);
        });

        // mandatory fields must always be visible to every role
        ROLES.forEach((role) => {
          mandatory.forEach((f) => {
            if (allFields.includes(f)) sets[role].add(f);
          });
        });

        setSelectedFields(sets);
      } catch (err) {
        console.error(err);
        setError("Failed to load field structure.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const activeRole = ROLES[activeTab];

  const toggleField = (field) => {
    if (mandatory.includes(field)) return;
    setSelectedFields((prev) => {
      const updated = new Set(prev[activeRole]);
      if (updated.has(field)) updated.delete(field);
      else updated.add(field);
      return { ...prev, [activeRole]: updated };
    });
  };

  const selectAll = () => {
    setSelectedFields((prev) => ({
      ...prev,
      [activeRole]: new Set(fields),
    }));
  };

  const clearAll = () => {
    setSelectedFields((prev) => ({
      ...prev,
      [activeRole]: new Set(mandatory.filter((f) => fields.includes(f))),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = [];
      ROLES.forEach((role) => {
        const roleFields = new Set(selectedFields[role]);
        mandatory.forEach((f) => {
          if (fields.includes(f)) roleFields.add(f);
        });
        roleFields.forEach((fieldName) => {
          payload.push({ projectId, fieldName, userRoleName: role });
        });
      });

      if (permissionsExist) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/permission`,
          payload,
          { headers },
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/permission`,
          payload,
          { headers },
        );
        setPermissionsExist(true);
      }
      showModal("Permissions saved successfully", "success", () =>
        navigate("/dashboard/projects"),
      );
    } catch (err) {
      console.error(err);
      showModal(err?.response?.data?.Exception || "Failed to save permissions", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1.5} p={4} sx={{ fontFamily: FONT }}>
        <CircularProgress size={20} sx={{ color: PRIMARY }} />
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: MUTED }}>Loading permissions…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 3, fontFamily: FONT, fontSize: 13.5, borderRadius: "12px", border: "1px solid #F2CFCA", background: "#FBEDEB", color: "#A6342A" }}
      >
        {error}
      </Alert>
    );
  }

  const activeSelected = selectedFields[activeRole];
  const allSelected = activeSelected.size === fields.length;

  return (
    <Box sx={{ p: 0, fontFamily: FONT, color: INK, textAlign: "left" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}>
            Field Visibility
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: "#4A5A6E", mt: 0.5 }}>
            Select which fields each role can see. Admin always sees everything.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon sx={{ fontSize: "17px !important" }} />
            )
          }
          onClick={handleSave}
          disabled={saving}
          sx={{
            height: 40,
            px: 2,
            borderRadius: "10px",
            background: PRIMARY,
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: "#084767", boxShadow: "none" },
            "&.Mui-disabled": { background: PRIMARY, color: "#FFFFFF", opacity: 0.6 },
          }}
        >
          {saving ? "Saving…" : "Save Permissions"}
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${LINE}`, boxShadow: "none" }}
      >
        {/* Role tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            borderBottom: `1px solid ${LINE}`,
            px: 1,
            minHeight: 48,
            background: "#F7F9FC",
            "& .MuiTabs-indicator": { background: PRIMARY, height: 2, borderRadius: 2 },
          }}
        >
          {ROLES.map((role, i) => (
            <Tab
              key={role}
              sx={{
                minHeight: 48,
                textTransform: "none",
                fontFamily: FONT,
                fontSize: 13.5,
                fontWeight: activeTab === i ? 700 : 500,
                color: `${activeTab === i ? INK : "#4A5A6E"} !important`,
              }}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {ROLE_LABELS[role]}
                  <Chip
                    label={selectedFields[role].size}
                    size="small"
                    sx={{
                      height: 20,
                      fontFamily: MONO,
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: "999px",
                      background: activeTab === i ? "#EAF2FB" : "#F1F4F8",
                      color: activeTab === i ? "#1B4F82" : "#5A6779",
                      border: `1px solid ${activeTab === i ? "#C9DEF3" : LINE}`,
                    }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Select all / clear */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          px={2}
          py={1.25}
          sx={{ borderBottom: "1px solid #EDF1F5" }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#4A5A6E" }}>
            <strong style={{ color: INK, fontFamily: MONO }}>{activeSelected.size}</strong> of{" "}
            <span style={{ fontFamily: MONO }}>{fields.length}</span> fields visible
          </Typography>
          <Button
            size="small"
            onClick={allSelected ? clearAll : selectAll}
            sx={{
              height: 30,
              px: 1.5,
              borderRadius: "8px",
              border: "1px solid #D5DEE7",
              color: INK,
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#F4F7FA" },
            }}
          >
            {allSelected ? "Clear All" : "Select All"}
          </Button>
        </Box>

        {/* Field list */}
        <Box p={2} display="flex" flexWrap="wrap" gap={1}>
          {fields.map((field) => {
            const isMandatory = mandatory.includes(field);
            const isSelected = isMandatory || activeSelected.has(field);
            return (
              <Box
                key={field}
                onClick={() => toggleField(field)}
                title={isMandatory ? "Required field — always visible" : ""}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.875,
                  borderRadius: "9px",
                  border: "1px solid",
                  borderColor: isSelected ? (isMandatory ? LINE : "#9FC3EA") : LINE,
                  background: isSelected ? (isMandatory ? "#F1F4F8" : "#EAF2FB") : "#fff",
                  cursor: isMandatory ? "not-allowed" : "pointer",
                  opacity: 1,
                  userSelect: "none",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: isMandatory ? (isSelected ? LINE : LINE) : "#9FC3EA",
                    background: isMandatory
                      ? isSelected
                        ? "#F1F4F8"
                        : "#fff"
                      : isSelected
                        ? "#EAF2FB"
                        : "#F4F9FE",
                  },
                }}
              >
                {isSelected ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: isMandatory ? "#8494A8" : PRIMARY }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: 16, color: "#C3CDD9" }}
                  />
                )}
                <Typography
                  sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: isSelected ? 600 : 500, color: isSelected ? INK : "#4A5A6E" }}
                >
                  {field}
                  {isMandatory && (
                    <span style={{ color: "#C42B24", marginLeft: 4 }}>*</span>
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Admin note */}
        <Divider sx={{ borderColor: "#EDF1F5" }} />
        <Box px={2} py={1.25} sx={{ background: "#FDF4E1" }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#4A5A6E", textWrap: "pretty" }}>
            <strong style={{ color: INK }}>Admin</strong> always has visibility into all{" "}
            {fields.length} fields and cannot be restricted.
          </Typography>
        </Box>
      </Paper>

      <ErrorModal
        open={modalOpen}
        handleClose={handleModalClose}
        msg={modalMsg}
        type={modalType}
      />
    </Box>
  );
};

export default LeadFieldPermission;